import type { ReelItem } from '~/components/PropertyReelsFeed/types';
import type { ShortVideo } from '~/composables/shorts/useFetchShorts';
import type { Property } from '~/core/entities/property';

import { useFeedState, useVideoHistory } from '~/composables/shorts';
import { propertyImages } from '~/composables/shorts/mappers';
import { shortVideoToReelItem } from '~/composables/shorts/useVideoHistory';
import { useShortsFeedStore } from '~/store/shortsFeedStore';
import { useVideoHistoryStore } from '~/store/videoHistoryStore';
import { nuxtPropertiesRepository } from '~~/adapters/repositories/nuxt';
import { storeToRefs } from 'pinia';
import { toRaw } from 'vue';

export const END_OF_RECOMMENDATION_CAROUSEL_COUNT = 3;

interface SetTargetFromPropertyOptions {
	countsTowardEndOfRecommendation?: boolean;
}

function mapPropertyToReelItem(property: Property): ReelItem | null {
	const featuredVideo = property.featuredVideos?.[0];
	if (featuredVideo) {
		return {
			videoUrl: featuredVideo.url,
			thumbnailUrl: featuredVideo.thumbnail ?? null,
			property: toRaw(property),
			videoType: 'native',
			videoWidth: featuredVideo.width,
			videoHeight: featuredVideo.height
		};
	}

	const images = propertyImages(property);
	if (!images.length) return null;

	return {
		videoUrl: null,
		thumbnailUrl: images[0] ?? null,
		images,
		property: toRaw(property),
		videoType: 'native'
	};
}

export function useShortsFeedOrchestrator() {
	const feedStore = useShortsFeedStore();
	const historyStore = useVideoHistoryStore();

	const { loadHistory, loadMoreHistory, resetHistory } = useVideoHistory();
	const {
		loadMore: loadMoreFeed,
		reset: resetFeed,
		seedFromCarousel
	} = useFeedState();

	const {
		videos,
		hasMore: hasMorePages,
		allWatched,
		isLoading: isFeedLoading,
		recommendationCountVideos,
		recommendationCountPhotos
	} = storeToRefs(feedStore);
	const { historyItems, hasMoreHistory, isLoadingHistory } =
		storeToRefs(historyStore);

	const targetItem = ref<ReelItem | null>(null);
	const targetCountsTowardEndOfRecommendation = ref(false);
	const isTargetLoading = ref(false);
	const targetCommercialId = computed(
		() => targetItem.value?.property.commercialId
	);

	const feedItems = computed(() => videos.value.map(shortVideoToReelItem));
	const recommendationFeedItems = computed(() => {
		if (
			!targetCountsTowardEndOfRecommendation.value ||
			!targetCommercialId.value
		) {
			return feedItems.value;
		}

		return feedItems.value.filter(
			item => item.property.commercialId !== targetCommercialId.value
		);
	});
	const recommendationItems = computed(() => {
		if (!targetCountsTowardEndOfRecommendation.value || !targetItem.value) {
			return recommendationFeedItems.value;
		}

		return [targetItem.value, ...recommendationFeedItems.value];
	});
	const endOfRecommendationCarouselStartIndex = computed(() => {
		for (
			let index = recommendationItems.value.length - 1;
			index >= 0;
			index -= 1
		) {
			if (recommendationItems.value[index]?.videoUrl) return index + 1;
		}

		return 0;
	});
	const endOfRecommendationCarousels = computed(() =>
		recommendationItems.value.slice(
			endOfRecommendationCarouselStartIndex.value,
			endOfRecommendationCarouselStartIndex.value +
				END_OF_RECOMMENDATION_CAROUSEL_COUNT
		)
	);
	const hasReachedEndOfRecommendation = computed(() => {
		if (isFeedLoading.value || !allWatched.value) {
			return false;
		}

		const carousels = endOfRecommendationCarousels.value;

		if (carousels.length === 0) return false;

		// Normalmente exige os 3 carrosséis finais completos, mas uma entrada
		// "semeada" (ex.: clique num item perto do fim da grade de
		// /shorts/home, onde o alvo conta pro fim de recomendação) pode
		// deixar menos que isso pra recomendar, sem mais nada a carregar.
		// Nesse caso específico, ainda é fim de recomendação — só com menos
		// itens sobrando. Fora dessa situação, continua exigindo os 3 cheios.
		const canRelyOnSeededTail =
			targetCountsTowardEndOfRecommendation.value && !hasMorePages.value;

		if (
			carousels.length < END_OF_RECOMMENDATION_CAROUSEL_COUNT &&
			!canRelyOnSeededTail
		) {
			return false;
		}

		return carousels.every(item => item.videoUrl == null);
	});
	const visibleFeedItems = computed(() => {
		const visibleRecommendationItems = hasReachedEndOfRecommendation.value
			? recommendationItems.value.slice(
					0,
					endOfRecommendationCarouselStartIndex.value +
						END_OF_RECOMMENDATION_CAROUSEL_COUNT
				)
			: recommendationItems.value;

		return targetCountsTowardEndOfRecommendation.value && targetItem.value
			? visibleRecommendationItems.slice(1)
			: visibleRecommendationItems;
	});
	const hasMore = computed(
		() => hasMorePages.value && !hasReachedEndOfRecommendation.value
	);
	const visibleHistoryItems = computed(() => {
		const seenCommercialIds = new Set<string>();
		const targetId = targetCommercialId.value;
		const items: ReelItem[] = [];

		for (const item of historyItems.value) {
			const commercialId = item.property.commercialId;
			if (commercialId === targetId) continue;
			if (seenCommercialIds.has(commercialId)) continue;

			seenCommercialIds.add(commercialId);
			items.push(item);
		}

		return items;
	});
	const historyCount = computed(() => visibleHistoryItems.value.length);

	const reelItems = computed(() => {
		const merged: ReelItem[] = [];

		for (const item of visibleHistoryItems.value) {
			merged.push({ ...item, reelKey: `h:${item.property.commercialId}` });
		}

		if (targetItem.value) {
			merged.push({
				...targetItem.value,
				reelKey: `t:${targetItem.value.property.commercialId}`
			});
		}

		for (const item of visibleFeedItems.value) {
			merged.push({ ...item, reelKey: `f:${item.property.commercialId}` });
		}

		return merged;
	});

	const isLoading = computed(
		() => isTargetLoading.value || isFeedLoading.value || isLoadingHistory.value
	);

	async function loadTargetItem(commercialId: string): Promise<void> {
		const userSession = useUserSession();
		const getCookieData = useCookieData();

		isTargetLoading.value = true;
		try {
			const [property, error] = await nuxtPropertiesRepository.getOne(
				commercialId,
				userSession.user.value?.id,
				getCookieData()
			);

			if (error || !property) {
				throw createError({
					statusCode: 404,
					statusMessage: 'Imóvel não encontrado'
				});
			}

			const mappedItem = mapPropertyToReelItem(property);
			targetItem.value = mappedItem;
			targetCountsTowardEndOfRecommendation.value = false;
		} finally {
			isTargetLoading.value = false;
		}
	}

	/**
	 * Seed-path variant: sets the entry target straight from the carousel's
	 * ShortVideo item, so an internal navigation renders the player without
	 * waiting for the property payload (which hydrates in the background).
	 */
	function setTargetFromShortVideo(
		item: ShortVideo,
		options: SetTargetFromPropertyOptions = {}
	): void {
		if (!item.videoUrl) return;
		targetItem.value = {
			videoUrl: item.videoUrl,
			thumbnailUrl: item.thumbnailUrl ?? null,
			property: toRaw(item.property),
			videoType: 'native',
			videoWidth: item.videoWidth,
			videoHeight: item.videoHeight
		};
		targetCountsTowardEndOfRecommendation.value =
			options.countsTowardEndOfRecommendation ?? false;
	}

	function setTargetFromProperty(
		property: Property,
		options: SetTargetFromPropertyOptions = {}
	): void {
		const mappedItem = mapPropertyToReelItem(property);
		if (!mappedItem) {
			throw createError({
				statusCode: 404,
				statusMessage: 'Imóvel sem short disponível'
			});
		}
		targetItem.value = mappedItem;
		targetCountsTowardEndOfRecommendation.value =
			options.countsTowardEndOfRecommendation ?? false;
	}

	function resetAll(): void {
		targetItem.value = null;
		targetCountsTowardEndOfRecommendation.value = false;
		resetHistory();
		resetFeed();
	}

	function resetHistoryAndFeed(): void {
		resetHistory();
		resetFeed();
	}

	return {
		reelItems,
		historyCount,
		hasMore,
		hasReachedEndOfRecommendation,
		hasMoreHistory,
		isLoading,
		recommendationCountVideos,
		recommendationCountPhotos,
		loadHistory,
		loadMoreHistory,
		loadMoreFeed,
		loadTargetItem,
		setTargetFromProperty,
		setTargetFromShortVideo,
		resetAll,
		resetHistoryAndFeed,
		seedFromCarousel
	};
}
